(function () {
  // Use an IIFE (Immediately Invoked Function Expression) to avoid polluting the global scope
  const STORAGE_KEY = 'bikeSizingData';
  const HIGHLIGHT_STATE_KEY = 'bikeSizingHighlightActive'; // Key for toggle state
  const RECOMMENDED_ICON_HTML = `
    <span class="variant-item__recommended-icon" aria-label="Recommended Size" style="display: inline-flex; align-items: center; margin-left: 4px; vertical-align: middle;">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="10" fill="#248A3D"/>
        <path d="M10.6208 6.271L11.6151 7.26521" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M9.12915 7.7627L10.1234 8.75691" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.6377 9.25439L8.63191 10.2486" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6.146 10.7456L7.14021 11.7398" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.40576 12.4857L12.3607 4.53076L15.3438 7.51387L7.38887 15.4688L4.40576 12.4857Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
  `;

  // Centralized Selectors
  const selectors = {
    productCardForm: 'form.product-card__form',
    productCard: '.product-card',
    variantInput: 'input[name="id"][type="radio"]',
    variantInputChecked: 'input[name="id"]:checked',
    variantButtonLabel: '.variant-buttons__button',
    variantItem: '.variant-buttons__item',
    variantList: '.variant-buttons__list',
    recommendedItemClass: 'variant-buttons__item--recommended',
    recommendedItemSelector: '.variant-buttons__item--recommended',
    recommendedIcon: '.variant-item__recommended-icon',
    submitButton: '.product-card__submit',
    submitButtonText: '.btn__text',
    scrollTargetInRecommended: 'label, button',
    sizingTool: '[data-sizing-tool]',
  };

  /**
   * Loads sizing measurements (especially adjustedHeight) from local storage.
   * @returns {object|null} An object like { height, inseam, adjustedHeight } or null if not found/error.
   */
  function loadSizingMeasurements() {
    try {
      let data = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!data) {
        data = retrieveSizingToolData();
      }

      // Check if essential data exists
      if (data && data.height && data.inseam && data.adjustedHeight) {
        return {
          height: parseFloat(data.height),
          inseam: parseFloat(data.inseam),
          adjustedHeight: parseFloat(data.adjustedHeight),
        };
      } else {
        return null;
      }
    } catch (e) {
      // Keep error logs for actual errors
      return null;
    }
  }

  function retrieveSizingToolData() {
    const sizingTool = document.querySelector(selectors.sizingTool);
    if (sizingTool && sizingTool.dataset.height && sizingTool.dataset.inseam) {
      return {
        height: sizingTool.dataset.height,
        inseam: sizingTool.dataset.inseam,
        adjustedHeight:
          sizingTool.dataset.height +
          (sizingTool.dataset.inseam - sizingTool.dataset.height * 0.47) * 0.5,
      };
    }

    return {};
  }

  /**
   * Updates the visual state (active class) of variant buttons.
   * @param {HTMLElement} formElement - The product card form element.
   * @param {string|null} selectedVariantId - The ID of the variant to mark as active, or null to deactivate all.
   */
  function updateCardVisuals(formElement, selectedVariantId) {
    const variantButtons = formElement.querySelectorAll(selectors.variantButtonLabel);
    variantButtons.forEach(button => {
      const radioId = button.getAttribute('for');
      const radio = formElement.querySelector(`#${radioId}`);
      button.classList.toggle(
        'variant-buttons__button--active',
        radio?.value === selectedVariantId
      );
    });
  }

  /**
   * Updates the Add To Cart button's state (enabled/disabled, text).
   * @param {HTMLElement} formElement - The product card form element.
   * @param {string|null} selectedVariantId - The ID of the selected variant.
   * @param {Array} variants - The array of variant objects for this product.
   * @param {object} translations - Object containing translations for ATC button text.
   */
  function updateCardATCButton(formElement, selectedVariantId, variants, translations) {
    const submitButton = formElement.querySelector(selectors.submitButton);
    const submitButtonText = submitButton?.querySelector(selectors.submitButtonText);
    if (!submitButton || !submitButtonText) {
      return;
    }

    const hasOnlyDefaultVariant =
      variants.length === 1 && variants[0].option1.toString() === 'Default Title';

    const selectedVariant =
      variants.length === 1 && hasOnlyDefaultVariant
        ? variants[0]
        : variants.find(v => v.id.toString() === selectedVariantId);
    const isAvailable = selectedVariant?.available || false;
    const isSelected = typeof selectedVariant !== 'undefined';

    submitButton.disabled = !isAvailable;
    submitButtonText.textContent = isAvailable
      ? translations.addToCart
      : isSelected
      ? translations.soldOut
      : translations.selectSize;
  }

  /**
   * Removes highlight class and icon from a product card form.
   * @param {HTMLElement} formElement - The product card form element.
   */
  function removeHighlightFromCard(formElement) {
    const recommendedItem = formElement.querySelector(selectors.recommendedItemSelector);
    if (recommendedItem) {
      recommendedItem.classList.remove(selectors.recommendedItemClass);
      const icon = recommendedItem.querySelector(selectors.recommendedIcon);
      if (icon) {
        icon.remove();
      }
    }
    // Also remove from any other items just in case (e.g., if structure changes)
    const existingIcons = formElement.querySelectorAll(selectors.recommendedIcon);
    existingIcons.forEach(icon => icon.remove());
    const existingHighlights = formElement.querySelectorAll(selectors.recommendedItemSelector);
    existingHighlights.forEach(item => item.classList.remove(selectors.recommendedItemClass));
  }

  /**
   * Finds the best matching variant item based on adjusted height for a specific product card.
   * @param {number} adjustedHeight - The calculated adjusted height.
   * @param {HTMLElement} formElement - The product card form element.
   * @returns {HTMLElement|null} The best matching variant item element or null.
   */
  function findBestVariantForCard(adjustedHeight, formElement) {
    const variantItems = formElement.querySelectorAll(selectors.variantItem);
    if (!variantItems || variantItems.length === 0 || isNaN(adjustedHeight)) {
      return null;
    }

    let potentialMatches = [];

    variantItems.forEach(item => {
      const minHeightStr = item.dataset.minHeight;
      const maxHeightStr = item.dataset.maxHeight;
      const minHeight = parseFloat(minHeightStr);
      const maxHeight = parseFloat(maxHeightStr);

      // Check for invalid or missing data for THIS variant item
      if (isNaN(minHeight) || isNaN(maxHeight) || minHeightStr === '' || maxHeightStr === '') {
        // console.warn('Invalid or missing height data on variant item:', item); // Optional warning
        return; // Skip this item for matching, but flag overall invalidity
      }

      if (adjustedHeight >= minHeight && adjustedHeight <= maxHeight) {
        const range = maxHeight - minHeight;
        const positionInRange = adjustedHeight - minHeight;
        let percentageInItem = range > 0 ? (positionInRange / range) * 100 : 50;
        percentageInItem = Math.max(0, Math.min(100, percentageInItem)); // Clamp

        potentialMatches.push({
          itemElement: item,
          centrality: Math.abs(percentageInItem - 50),
        });
      }
    });

    // Cannot determine best match if no matches found
    if (potentialMatches.length === 0) {
      return null;
    }

    // Find best match (closest to center of its range)
    if (potentialMatches.length > 1) {
      potentialMatches.sort((a, b) => a.centrality - b.centrality);
    }

    return potentialMatches[0].itemElement; // Return the DOM element
  }

  /**
   * Initializes a single product card form: adds icon, sets initial state, attaches listener.
   * @param {HTMLElement} formElement - The product card form element to initialize.
   */
  function initializeProductCard(formElement) {
    // Always remove existing highlight first
    removeHighlightFromCard(formElement);

    // Read the toggle state
    const isHighlightingEnabled = localStorage.getItem(HIGHLIGHT_STATE_KEY) === 'true';

    // Load measurements
    const measurements = loadSizingMeasurements();
    const adjustedHeight = measurements ? measurements.adjustedHeight : null;

    const productId = formElement.dataset.productId;
    const variantsJson = formElement.dataset.variantsJson;
    const translations = {
      addToCart: formElement.dataset.translationAddToCart || 'Add to Cart',
      soldOut: formElement.dataset.translationSoldOut || 'Sold Out',
      selectSize: formElement.dataset.translationSelectSize || 'Select Size',
    };
    let variants = [];

    try {
      variants = JSON.parse(variantsJson || '[]');
    } catch (e) {
      return;
    }
    if (!productId || !variants.length) {
      return;
    }

    const radios = formElement.querySelectorAll(selectors.variantInput);
    let initialVariantSelected = false;
    let selectedVariantId = null;
    // --- Determine Initial Selection ---
    // 1. Try selecting based on calculated best fit IF highlighting is enabled
    if (adjustedHeight !== null && isHighlightingEnabled) {
      const bestVariantItemElement = findBestVariantForCard(adjustedHeight, formElement);

      if (bestVariantItemElement) {
        // Add highlight class
        bestVariantItemElement.classList.add(selectors.recommendedItemClass);

        // Add icon if it doesn't exist
        const iconAlreadyExists = bestVariantItemElement.querySelector(selectors.recommendedIcon);
        if (!iconAlreadyExists) {
          bestVariantItemElement.insertAdjacentHTML('beforeend', RECOMMENDED_ICON_HTML);
        }

        // Find and check the corresponding radio button
        const label = bestVariantItemElement.querySelector('label');
        const radioId = label?.getAttribute('for');
        const recommendedRadio = radioId ? formElement.querySelector(`#${radioId}`) : null;

        if (recommendedRadio) {
          recommendedRadio.checked = true;
          recommendedRadio.dispatchEvent(new Event('change', { bubbles: true }));
          initialVariantSelected = true;
          selectedVariantId = recommendedRadio.value;
        } else {
          // console.warn('Could not find radio button for recommended variant item', bestVariantItemElement);
        }
      }
    }

    // 2. Update button/visuals based on the final selection (or lack thereof)
    // This needs to happen *after* both recommendation and fallback logic
    if (initialVariantSelected) {
      updateCardATCButton(formElement, selectedVariantId, variants, translations);
      updateCardVisuals(formElement, selectedVariantId);
    } else {
      let selectedVariantId = null;
      // Find the user selected radio, otherwise selectedVariant is null
      radios.forEach(radio => {
        if (radio.checked) {
          selectedVariantId = radio.value;
        }
      });

      // Handle truly no selection case (e.g., no variants, or first variant couldn't be selected)
      updateCardATCButton(formElement, selectedVariantId, variants, translations);
      updateCardVisuals(formElement, selectedVariantId);
    }

    // Attach event listener for user changes *to this specific form*
    if (!formElement.dataset.plpFormInitialized) {
      formElement.addEventListener('change', event => {
        if (event.target.name === 'id' && event.target.matches(selectors.variantInput)) {
          const newSelectedId = event.target.value;
          updateCardATCButton(formElement, newSelectedId, variants, translations);
          updateCardVisuals(formElement, newSelectedId);
        }
      });

      // Enable drag scroll for the variant list in this card ONLY for non-touch/mouse devices
      const variantListContainer = formElement.querySelector(selectors.variantList);
      if (variantListContainer && window.matchMedia('(pointer: fine)').matches) {
        enableDragScroll(variantListContainer);
      }

      formElement.dataset.plpFormInitialized = 'true'; // Mark as initialized
    }
  }

  /**
   * Finds and initializes all product card forms within a given container.
   * @param {Element|Document} container - The element to search within (defaults to document).
   */
  function initializeAllProductCards(container = document) {
    const productForms = container.querySelectorAll(selectors.productCardForm);
    productForms.forEach(initializeProductCard);
  }

  // --- Global Event Listeners ---
  document.addEventListener('collection:rerendered', function (event) {
    if (typeof initializeAllProductCards === 'function') {
      initializeAllProductCards();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof initializeAllProductCards === 'function') {
      initializeAllProductCards();
    }
  });

  function scrollRecommendedVariantIntoView(productCardElement) {
    const scrollContainer = productCardElement.querySelector(selectors.variantList);
    const recommendedItem = productCardElement.querySelector(selectors.recommendedItemSelector);

    if (scrollContainer && recommendedItem) {
      const targetElement =
        recommendedItem.querySelector(selectors.scrollTargetInRecommended) || recommendedItem;

      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(selectors.productCard).forEach(card => {
      if (card.querySelector(selectors.variantList)) {
        scrollRecommendedVariantIntoView(card);
      }
    });
  });

  /**
   * Enables click-and-drag scrolling for a horizontal container (Direct manipulation).
   * @param {HTMLElement} container - The scrollable container element.
   */
  function enableDragScroll(container) {
    if (!container) return;
    container.classList.add('is-draggable');
    let isDown = false;
    let startX;
    let scrollLeft;

    const startDragging = e => {
      // Only activate for left mouse button and if overflow exists
      if (e.button !== 0 || container.scrollWidth <= container.clientWidth) return;
      isDown = true;
      container.classList.add('is-dragging');
      startX = e.pageX - container.offsetLeft; // Mouse position relative to container
      scrollLeft = container.scrollLeft; // Current scroll position
      e.preventDefault(); // Prevent text selection, etc.
    };

    const stopDragging = () => {
      if (!isDown) return;
      isDown = false;
      container.classList.remove('is-dragging');
    };

    const onDrag = e => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft; // Current mouse position relative to container
      const walk = x - startX; // How far the mouse has moved since mousedown
      container.scrollLeft = scrollLeft - walk; // Adjust scroll based on initial scroll and mouse delta
    };

    container.addEventListener('mousedown', startDragging);
    // Use window listeners for robust mouseup/leave detection
    window.addEventListener('mouseup', stopDragging);
    window.addEventListener('mouseleave', stopDragging); // Stop if mouse leaves window
    container.addEventListener('mouseleave', stopDragging); // Also stop if mouse leaves container specifically
    window.addEventListener('mousemove', onDrag);

    // Basic cleanup (consider more robust component lifecycle if needed)
    // Note: These window listeners might affect other drag operations if not scoped properly
    // or if multiple instances are created without cleanup.
  }

  // Expose utilities AND the helper function
  window.FoxTheme = window.FoxTheme || {};
  window.FoxTheme.RecommendedSizeUtils = {
    loadSizingMeasurements,
    RECOMMENDED_ICON_HTML,
    selectors,
  };

  // Expose the main initialization function for external calls (like toggling)
  window.FoxTheme.PLPFormUtils = {
    initializeAllProductCards,
  };

  // Add listener for updates from the sizing tool
  window.addEventListener('recommended-size-updated', () => {
    if (typeof initializeAllProductCards === 'function') {
      initializeAllProductCards();
    }
  });

  // Aligns product cards in a grid by equalizing heights of titles and variant selectors within each row.
  document.addEventListener('DOMContentLoaded', () => {
    const grid = document.querySelector('[data-products-grid]');
    if (!grid) return;

    let cards = Array.from(grid.querySelectorAll('[data-product-grid-item]'));

    const groupByRow = cards => {
      // Group product cards per row by top position
      const rows = [];
      cards.forEach(card => {
        const top = card.getBoundingClientRect().top + window.scrollY;
        const row = rows.find(r => r.top === top);
        if (row) {
          row.items.push(card);
        } else {
          rows.push({ top, items: [card] });
        }
      });
      return rows;
    };

    const setVariables = rows => {
      rows.forEach(({ items }) => {
        // Get tallest title in the row
        const maxTitle = Math.max(
          ...items.map(card => {
            const el = card.querySelector('[data-product-card-inner-title]');
            return el ? el.offsetHeight : 0;
          })
        );

        // Get max height for variants
        const maxVariants = Math.max(
          ...items.map(card => {
            const el = card.querySelector('[data-variant-button]');
            return el ? el.offsetHeight : 0;
          })
        );

        // Get max height for rating
        const maxRating = Math.max(
          ...items.map(card => {
            const el = card.querySelector('[data-product-card-rating]');
            return el ? el.offsetHeight : 0;
          })
        );

        // Get max height for price
        const maxPrice = Math.max(
          ...items.map(card => {
            const el = card.querySelector('[data-product-price]');
            return el ? el.offsetHeight : 0;
          })
        );

        // Apply per-card variables (0px when none of the row has that section)
        items.forEach(card => {
          const cardWrapper = card.querySelector('[data-product-card-wrapper]') || card;
          cardWrapper.style.setProperty('--height-title', `${maxTitle}px`);
          cardWrapper.style.setProperty('--height-variants', `${maxVariants}px`);
          cardWrapper.style.setProperty('--height-rating', `${maxRating}px`);
          cardWrapper.style.setProperty('--height-price', `${maxPrice}px`);
        });
      });
    };

    const initAlignment = () => {
      requestAnimationFrame(() => {
        cards = Array.from(grid.querySelectorAll('.card'));

        const rows = groupByRow(cards);
        setVariables(rows);
      });
    };

    if (cards.length > 0) {
      initAlignment();
    } else {
      // No cards found, wait for them to be added
      const callback = (mutationList, observer) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            break;
          }
        }

        observer.disconnect();

        initAlignment();
      };

      const gridObserver = new MutationObserver(callback);

      gridObserver.observe(grid, {
        childList: true,
        subtree: false,
      });
    }

    // Handle resize / media query change
    const gridResizeObserver = new ResizeObserver(initAlignment);
    gridResizeObserver.observe(grid);
  });
})();
